<template>
    <div
        v-bind="$attrs"
        class="ds-card-outer"
        :class="{
            'ds-card-disabled': disabled,
            'ds-card-interactive': variant === 'interactive',
            'ds-card-selected': variant === 'interactive' && selected,
            'ds-card-auto-focus': variant === 'interactive' && autoFocus,
            'ds-card-error': error,
            'ds-card-clickable': clickable,
        }"
    >
        <div
            class="ds-card-inner"
            :class="bgColor"
        >
            <slot />
        </div>
    </div>
    <div
        v-if="error && typeof error === 'string'"
        class="flex items-center gap-1 text-content-md text-content-error"
    >
        <DsIcon
            icon="fa-solid fa-warning"
            size="sm"
        />
        <slot name="error">
            {{ error }}
        </slot>
    </div>
</template>

<script setup lang="ts">
import DsIcon from '@/components/design-system/DsIcon.vue';

export interface DsCardProps {
    /** Pass in a Tailwind background color class to override the default white bg color; e.g. `bg-ds-primary-50` */
    bgColor?: string;
    /** Whether the card has an error state. */
    error?: string | boolean;
    /** The variant of the card. */
    variant?: 'plain' | 'interactive';
    /** Force card to adopt a selected state. This is only applicable to interactive cards. */
    selected?: boolean;
    /** Automatically focus the card when it is selected. This is only applicable to interactive cards. */
    autoFocus?: boolean;
    /** Whether the card is clickable and should present a pointer cursor. */
    clickable?: boolean;
    /** Whether the card is disabled (not focusable or clickable). */
    disabled?: boolean;
}

withDefaults(defineProps<DsCardProps>(), {
    variant: 'plain',
    selected: false,
    autoFocus: false,
    error: false,
    clickable: (props: DsCardProps) => props.variant === 'interactive',
    disabled: false,
});

defineSlots<{
    default: () => void;
    error: () => void;
}>();
</script>

<style scoped lang="scss">
.ds-card-outer {
    @apply border-2 border-transparent bg-transparent;

    // note: radius is 10px (8px for lg + 2px to account for border) so it hugs the card-inner when focused or active
    @apply rounded-lg;
}

.ds-card-inner {
    @apply h-full rounded-md border border-ds-gray-200 bg-white p-4;
}

.ds-card-clickable {
    @apply cursor-pointer;
}

.ds-card-outline {
    @apply border-ds-gray-200 shadow-outline-field-4;
    transition: outline 0.1s ease-in-out;
}

.ds-card-disabled {
    @apply pointer-events-none cursor-not-allowed;

    .ds-card-inner {
        @apply bg-ds-gray-50;
    }
}

.ds-card-interactive {
    @apply border-transparent transition-all duration-75;

    .ds-card-outer:hover,
    .ds-card-inner:hover {
        @apply shadow-dropdown-menu;
    }

    &.ds-card-auto-focus:focus-within {
        &.ds-card-outer {
            @extend .ds-card-outline;
            @apply z-40;
            @apply border-ds-gray-200 shadow-outline-field-4;
        }
        &.ds-card-selected {
            @apply border-2 border-ds-gray-900;
        }
        &.ds-card-error {
            @extend .ds-card-error;
        }
    }

    &.ds-card-error {
        @extend .ds-card-error;
    }

    &.ds-card-selected {
        &.ds-card-outer {
            @apply border-2 border-ds-gray-900;
        }

        > .ds-card-inner {
            @apply border-transparent;
        }
    }
}

.ds-card-clickable:active {
    &.ds-card-outer {
        @extend .ds-card-outline;
        @apply border-2 border-ds-gray-700;
    }

    > .ds-card-inner {
        @apply border-transparent;
    }
}

.ds-card-error {
    @apply rounded-lg border-2 border-semantic-destructive-primary-default;

    > .ds-card-inner {
        @apply border-transparent;
    }
}
</style>
